import { createStore } from "./store-factory";
import { FormSchema, FormSubmissionData } from "../types/formSchema";
import { APIStatus } from "../utils/conditionalRenderer";

export interface FormStore {
  // State
  formData: Record<string, any>; // workflowId -> form data
  schemas: Record<string, FormSchema>; // cached schemas
  submissions: Record<string, FormSubmissionData>; // workflowId -> submission data
  loading: Record<string, boolean>; // workflowId -> loading state
  errors: Record<string, Record<string, string[]>>; // workflowId -> field errors
  apiStatuses: Record<string, Record<string, APIStatus>>; // workflowId -> fieldName -> API status

  // Actions
  setFormData: (workflowId: string, data: any) => void;
  updateFormField: (workflowId: string, fieldName: string, value: any) => void;
  getFormData: (workflowId: string) => any;
  clearFormData: (workflowId: string) => void;
  
  // Schema management
  setSchema: (workflowId: string, schema: FormSchema) => void;
  getSchema: (workflowId: string) => FormSchema | null;
  
  // Validation and errors
  setFieldError: (workflowId: string, fieldName: string, errors: string[]) => void;
  clearFieldError: (workflowId: string, fieldName: string) => void;
  getFieldErrors: (workflowId: string, fieldName: string) => string[];
  
  // Loading states
  setLoading: (workflowId: string, loading: boolean) => void;
  isLoading: (workflowId: string) => boolean;
  
  // API state management - Pure state operations only
  setAPIStatus: (workflowId: string, fieldName: string, state: APIStatus) => void;
  getAPIStatus: (workflowId: string, fieldName: string) => APIStatus;
  getAPIStatuses: (workflowId: string) => Record<string, APIStatus>;
  clearAPIStatus: (workflowId: string, fieldName: string) => void;
  
  // Submission management
  setSubmissionData: (workflowId: string, data: FormSubmissionData) => void;
  getSubmissionData: (workflowId: string) => FormSubmissionData | null;
}

/**
 * Form Store - Manages form state, validation, and submissions
 * Persisted in localStorage for draft functionality
 * Excludes loading states from persistence
 */
export const useFormStore = createStore<FormStore>(
  "formStore",
  (set, get) => ({
    // State
    formData: {},
    schemas: {},
    submissions: {},
    loading: {},
    errors: {},
    apiStatuses: {},

    // Actions
    setFormData: (workflowId: string, data: any) => {
      set(state => ({
        formData: {
          ...state.formData,
          [workflowId]: data,
        },
      }));
    },

    updateFormField: (workflowId: string, fieldName: string, value: any) => {
      set(state => ({
        formData: {
          ...state.formData,
          [workflowId]: {
            ...state.formData[workflowId],
            [fieldName]: value,
          },
        },
      }));
    },

    getFormData: (workflowId: string) => {
      return get().formData[workflowId] || {};
    },

    clearFormData: (workflowId: string) => {
      set(state => {
        const newFormData = { ...state.formData };
        delete newFormData[workflowId];
        return { formData: newFormData };
      });
    },

    // Schema management
    setSchema: (workflowId: string, schema: FormSchema) => {
      set(state => ({
        schemas: {
          ...state.schemas,
          [workflowId]: schema,
        },
      }));
    },

    getSchema: (workflowId: string) => {
      return get().schemas[workflowId] || null;
    },

    // Validation and errors
    setFieldError: (workflowId: string, fieldName: string, errors: string[]) => {
      set(state => ({
        errors: {
          ...state.errors,
          [workflowId]: {
            ...state.errors[workflowId],
            [fieldName]: errors,
          },
        },
      }));
    },

    clearFieldError: (workflowId: string, fieldName: string) => {
      set(state => {
        const workflowErrors = { ...state.errors[workflowId] };
        delete workflowErrors[fieldName];
        return {
          errors: {
            ...state.errors,
            [workflowId]: workflowErrors,
          },
        };
      });
    },

    getFieldErrors: (workflowId: string, fieldName: string) => {
      return get().errors[workflowId]?.[fieldName] || [];
    },

    // Loading states
    setLoading: (workflowId: string, loading: boolean) => {
      set(state => ({
        loading: {
          ...state.loading,
          [workflowId]: loading,
        },
      }));
    },

    isLoading: (workflowId: string) => {
      return get().loading[workflowId] || false;
    },

    // API state management
    setAPIStatus: (workflowId: string, fieldName: string, state: APIStatus) => {
      set(currentState => ({
        apiStatuses: {
          ...currentState.apiStatuses,
          [workflowId]: {
            ...currentState.apiStatuses[workflowId],
            [fieldName]: state,
          },
        },
      }));
    },

    getAPIStatus: (workflowId: string, fieldName: string) => {
      return get().apiStatuses[workflowId]?.[fieldName] ?? { status: 'idle' };
    },

    getAPIStatuses: (workflowId: string) => {
      return get().apiStatuses[workflowId] || {};
    },

    clearAPIStatus: (workflowId: string, fieldName: string) => {
      set(state => {
        const workflowAPIStatuses = { ...state.apiStatuses[workflowId] };
        delete workflowAPIStatuses[fieldName];
        return {
          apiStatuses: {
            ...state.apiStatuses,
            [workflowId]: workflowAPIStatuses,
          },
        };
      });
    },

    // Submission management
    setSubmissionData: (workflowId: string, data: FormSubmissionData) => {
      set(state => ({
        submissions: {
          ...state.submissions,
          [workflowId]: data,
        },
      }));
    },

    getSubmissionData: (workflowId: string) => {
      return get().submissions[workflowId] || null;
    },
  }),
  {
    persist: true,
    persistKey: "finSpace_forms",
    excludeFields: ["loading"], // Don't persist loading states
  }
);
